import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, catchError, map, Observable, of, shareReplay, tap } from 'rxjs';

export interface FeatureFlags {
  timeNotes: boolean;
  projectBudgetInTime: boolean;
  internalTimeLock: boolean;
  overtimeQuickAdd: boolean;
}

const DEFAULT_FEATURE_FLAGS: FeatureFlags = {
  timeNotes: false,
  projectBudgetInTime: false,
  internalTimeLock: false,
  overtimeQuickAdd: false,
};

@Injectable({
  providedIn: 'root',
})
export class FeatureFlagsService {
  private readonly flags$ = new BehaviorSubject<FeatureFlags>(DEFAULT_FEATURE_FLAGS);
  private loadRequest$?: Observable<FeatureFlags>;

  constructor(@Inject(HttpClient) private http: HttpClient) {}

  load(force = false): Observable<FeatureFlags> {
    if (force) {
      this.loadRequest$ = undefined;
    }

    if (!this.loadRequest$) {
      this.loadRequest$ = this.http.get<Partial<FeatureFlags>>('api/features', { withCredentials: true }).pipe(
        map((flags) => this.normalize(flags)),
        catchError(() => of(DEFAULT_FEATURE_FLAGS)),
        tap((flags) => this.flags$.next(flags)),
        shareReplay(1)
      );
    }

    return this.loadRequest$;
  }

  getFlags$(): Observable<FeatureFlags> {
    return this.flags$.asObservable();
  }

  isEnabled(key: keyof FeatureFlags): boolean {
    return !!this.flags$.value[key];
  }

  private normalize(flags?: Partial<FeatureFlags> | null): FeatureFlags {
    return {
      timeNotes: !!flags?.timeNotes,
      projectBudgetInTime: !!flags?.projectBudgetInTime,
      internalTimeLock: !!flags?.internalTimeLock,
      overtimeQuickAdd: !!flags?.overtimeQuickAdd,
    };
  }
}
